import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { createClient, defineChain } from "viem";
import { createConfig, http, WagmiProvider } from "wagmi";
import {
  Chain,
  mainnet,
  polygon,
  polygonAmoy,
  sepolia,
  base,
  baseSepolia,
  arbitrum,
  arbitrumSepolia,
  avalanche,
  avalancheFuji,
  optimismSepolia,
  bscTestnet,
  optimism,
  bsc,
} from "wagmi/chains";
import { metaMask, safe, walletConnect } from "wagmi/connectors";
import { config } from "../config";

const plumeTestnetNetwork = defineChain({
  id: 98867,
  name: "Plume Testnet",
  nativeCurrency: {
    decimals: 18,
    name: "Plume",
    symbol: "PLUME",
  },
  rpcUrls: {
    default: {
      http: ["https://testnet-rpc.plume.org"],
    },
  },
  blockExplorers: {
    default: { name: "Explorer", url: "https://testnet-explorer.plume.org" },
  },
});

const plumeMainnetNetwork = defineChain({
  id: 98866,
  name: "Plume",
  nativeCurrency: {
    decimals: 18,
    name: "Plume",
    symbol: "PLUME",
  },
  rpcUrls: {
    default: {
      http: ["https://rpc.plume.org"],
    },
  },
  blockExplorers: {
    default: { name: "Explorer", url: "https://explorer.plume.org" },
  },
});

const selectedChains: readonly [Chain, ...Chain[]] =
  config.NODE_ENV === "dev"
    ? [
        polygonAmoy,
        sepolia,
        arbitrumSepolia,
        baseSepolia,
        avalancheFuji,
        optimismSepolia,
        bscTestnet,
        plumeTestnetNetwork,
      ]
    : [
        polygon,
        mainnet,
        arbitrum,
        base,
        avalanche,
        optimism,
        bsc,
        plumeMainnetNetwork,
      ];

export const configWagmi = createConfig({
  chains: selectedChains,
  connectors: [
    metaMask({
      storage: {
        enabled: false,
      },
    }),
    walletConnect({
      projectId: config.WALLET_CONNECT_PROJECT_ID,
      showQrModal: false,
    }),
    safe({ allowedDomains: [/app.safe.global$/], debug: false }),
  ],
  client({ chain }: { chain: Chain }) {
    return createClient({ chain, transport: http() });
  },
  multiInjectedProviderDiscovery: false,
});

const queryClient = new QueryClient();

interface IWalletProvider {
  children: React.ReactNode;
}

export const WalletProvider = ({ children }: IWalletProvider) => (
  <WagmiProvider config={configWagmi}>
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  </WagmiProvider>
);
